import React from "react";
import axios from "axios";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { API, fmtDay, pct } from "@/components/fbShared";
import { GameCard } from "@/components/FirstBucketGames";
import { PlayerBoard, RankList } from "@/components/FirstBucketBoard";
import { PlayerDrawer } from "@/components/FirstBucketPlayer";
import { Loading, OfficialCard, WeaknessMatrix } from "@/components/FirstBucketTables";
import { FirstBucketAudit, FirstBucketSources } from "@/components/FirstBucketAudit";
import { PairsBoard, PairsAudit } from "@/components/FirstBucketPairs";

const TABS = [["board", "Board"], ["games", "Games & Tip-Off"], ["weakness", "Opponent Weakness"], ["card", "Official Card"], ["audit", "Audit"], ["sources", "Data Sources"]];
const MODE_CLS = { "Live-Validated": "text-emerald-300", Projected: "text-violet-300", Historical: "text-slate-300" };

const Card = ({ k, v, testId }) => (
  <div className="rounded-xl border border-slate-800 bg-[#0B0F17] p-3" data-testid={testId}>
    <p className="font-mono text-[9px] uppercase tracking-wider text-slate-500">{k}</p><p className="font-display text-2xl font-bold text-orange-200">{v}</p>
  </div>
);

const Header = ({ d, date, setDate, reload }) => {
  const bad = Object.entries(d?.health || {}).filter(([, v]) => !v.ok);
  return (
    <div className="mb-4 space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <p className="font-display text-sm font-bold uppercase tracking-wide text-orange-300" data-testid="fb-title">{d?.engine || "NBA First Basket Engine v1.0"}</p>
          <p className="font-mono text-[10px] text-slate-500">{d ? `${d.date.slice(0, 4)}-${d.date.slice(4, 6)}-${d.date.slice(6)} · ${d.season - 1}-${String(d.season).slice(2)} season` : ""} · no sportsbook odds</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} data-testid="fb-date" className="h-9 rounded-md border border-slate-800 bg-slate-950 px-2 font-mono text-xs text-slate-200" />
          <button type="button" onClick={reload} data-testid="fb-refresh" className="rounded-md border border-slate-800 p-2 text-slate-400 hover:text-orange-300"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>
      {d && (
        <p className="flex flex-wrap gap-x-3 font-mono text-[10px] text-slate-500" data-testid="fb-freshness">
          <span>Mode: <b className={MODE_CLS[d.mode]} data-testid="fb-mode">{d.mode}</b></span>
          <span>History: {d.history_games} games</span>
          <span>Model built: {d.model_built_at ? new Date(d.model_built_at).toLocaleString() : "N/A"}</span>
          <span>Probabilities: {d.calibrated ? "calibrated on backtest" : "MODEL RANKING — NOT CALIBRATED"}</span>
          {d.ingest.running && <span className="text-amber-300">Ingesting history: {d.ingest.progress}</span>}
          <span className={bad.length ? "text-rose-400" : "text-emerald-400"} data-testid="fb-health">{bad.length ? `Source issue: ${bad.map(([k]) => k).join(", ")}` : "All sources OK"}</span>
        </p>
      )}
      {d?.retro && <p className="flex items-center gap-1 font-mono text-[10px] text-amber-300" data-testid="fb-retro"><AlertTriangle className="h-3 w-3" /> Past date: rankings are a retrospective view built with the current model. Honest pre-game picks are in the Audit tab.</p>}
    </div>
  );
};

export default function FirstBucketTab() {
  const [date, setDate] = React.useState(fmtDay(new Date()));
  const [tab, setTab] = React.useState("board");
  const [d, setD] = React.useState(null);
  const [pid, setPid] = React.useState(null);
  const latest = React.useRef(date);
  latest.current = date;
  const load = React.useCallback(() => {
    const keep = (v) => { if (latest.current === date) setD(v); };
    axios.get(`${API}/slate`, { params: { date: date.replace(/-/g, "") } }).then((r) => keep(r.data)).catch(() => keep({ error: true }));
  }, [date]);
  React.useEffect(() => { setD(null); load(); const t = setInterval(load, 90000); return () => clearInterval(t); }, [load]);
  const ok = d && !d.error;
  return (
    <div data-testid="first-bucket-tab">
      <Header d={ok ? d : null} date={date} setDate={setDate} reload={load} />
      <div className="mb-4 flex flex-wrap gap-1.5">
        {TABS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`fb-tab-${k}`}
            className={`rounded-lg px-3 py-1.5 font-mono text-[11px] font-bold uppercase transition-colors ${tab === k ? "bg-orange-500 text-slate-950" : "border border-slate-800 text-slate-400 hover:text-orange-300"}`}>{l}</button>
        ))}
      </div>
      {tab === "weakness" ? <WeaknessMatrix date={date.replace(/-/g, "")} />
        : tab === "card" ? <OfficialCard date={date.replace(/-/g, "")} onPlayer={setPid} />
        : tab === "audit" ? <div className="space-y-3"><FirstBucketAudit date={date.replace(/-/g, "")} /><PairsAudit /></div>
        : tab === "sources" ? <FirstBucketSources />
        : !d ? <Loading />
        : d.error ? <p className="font-mono text-xs text-rose-400" data-testid="fb-error">FIRST BUCKET slate unavailable. <button type="button" onClick={load} className="underline">Retry</button></p>
        : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              <Card k="Games" v={d.summary.games} testId="fb-sum-games" /><Card k="Validated matchups" v={d.summary.validated} testId="fb-sum-validated" />
              <Card k="Approved" v={d.summary.approved} testId="fb-sum-approved" /><Card k="Top fusion" v={d.summary.top_score ?? "N/A"} testId="fb-sum-top" />
              <Card k="Data completeness" v={d.summary.completeness == null ? "N/A" : pct(d.summary.completeness, 0)} testId="fb-sum-complete" />
            </div>
            {!d.games.length && <p className="rounded-xl border border-slate-800 p-6 text-center font-mono text-xs text-slate-500" data-testid="fb-empty">No NBA games on this date.</p>}
            {tab === "games" ? (
              <div className="grid gap-3 lg:grid-cols-2">{d.games.map((g) => <GameCard key={g.game._id} g={g} onPlayer={setPid} />)}</div>
            ) : d.games.length > 0 && (
              <>
                <div className="grid gap-3 lg:grid-cols-2">
                  <RankList title="A · Most likely first basket" testId="fb-rank-likely" rows={d.most_likely} field="prob" onPlayer={setPid}
                    note={d.calibrated ? "Estimated probability: P(team scores first) × player's shrunk share — calibrated on the walk-forward backtest." : "MODEL RANKING — NOT CALIBRATED PROBABILITIES"}
                    fmt={(v) => (d.calibrated ? pct(v) : "rank")} />
                  <RankList title="B · Best first-basket matchup" testId="fb-rank-matchup" rows={d.best_matchup} field="matchup_score" onPlayer={setPid}
                    note="Alignment of tip edge, player history, opponent positional weakness, opening role and scoring method (0-100 index)." fmt={(v) => v ?? "N/A"} />
                </div>
                <PairsBoard date={date.replace(/-/g, "")} onPlayer={setPid} stamp={d.model_built_at} />
                <PlayerBoard rows={d.board} games={d.games} onPlayer={setPid} />
              </>
            )}
          </div>
        )}
      <PlayerDrawer pid={pid} date={date.replace(/-/g, "")} onClose={() => setPid(null)} />
    </div>
  );
}
